/* ===== custom-swiper-slider ===== */
if (!customElements.get('custom-swiper-slider')) {
  customElements.define(
    'custom-swiper-slider',
    class extends HTMLElement {
      connectedCallback() {
        this.swiperEl = this.querySelector('.swiper');
        this.init();
        if (window.Shopify && Shopify.designMode) {
          this.onSelect = () => this.swiper?.autoplay?.stop();
          this.onDeselect = () => this.swiper?.autoplay?.start();
          this.addEventListener('shopify:section:select', this.onSelect);
          this.addEventListener('shopify:section:deselect', this.onDeselect);
        }
      }
      disconnectedCallback() {
        this.swiper?.destroy(true, true);
        this.removeEventListener('shopify:section:select', this.onSelect);
        this.removeEventListener('shopify:section:deselect', this.onDeselect);
      }
      init() {
        if (typeof Swiper === 'undefined' || !this.swiperEl) return;
        const tag = this.querySelector('script[data-swiper-config]');
        let cfg = {};
        try {
          cfg = JSON.parse(tag ? tag.textContent : '{}');
        } catch (e) {
          cfg = {};
        }
        const next = this.querySelector('.swiper-button-next');
        const prev = this.querySelector('.swiper-button-prev');
        const pg = this.querySelector('.swiper-pagination');
        if (next && prev) {
          cfg.navigation = Object.assign({}, cfg.navigation, { nextEl: next, prevEl: prev });
        }
        if (pg && cfg.pagination) {
          cfg.pagination = Object.assign({ el: pg }, cfg.pagination);
        }
        if (cfg.loop) this.fillLoopSlides(cfg);
        cfg.a11y = Object.assign({ enabled: true }, cfg.a11y);
        cfg.keyboard = Object.assign({ enabled: true }, cfg.keyboard);
        if (cfg.autoplay && window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
          delete cfg.autoplay;
        }
        this.swiper = new Swiper(this.swiperEl, cfg);
      }
      // Swiper 11 loop mode breaks (esp. with centeredSlides) when there are fewer
      // slides than it needs to rebuild both sides; duplicate the set until there are enough.
      fillLoopSlides(cfg) {
        const wrapper = this.swiperEl.querySelector('.swiper-wrapper');
        if (!wrapper) return;
        const originals = Array.from(wrapper.children).filter((el) => el.classList.contains('swiper-slide'));
        if (originals.length < 2) return;
        const perView = [cfg.slidesPerView]
          .concat(Object.values(cfg.breakpoints || {}).map((bp) => bp.slidesPerView))
          .map((v) => parseFloat(v))
          .filter((v) => !isNaN(v));
        const maxPerView = perView.length ? Math.max(...perView) : 1;
        const needed = Math.ceil(maxPerView) * 2 + 1;
        let count = originals.length;
        while (count < needed) {
          originals.forEach((slide) => {
            const clone = slide.cloneNode(true);
            clone.removeAttribute('data-shopify-editor-block');
            clone.setAttribute('data-swiper-fill-clone', '');
            wrapper.appendChild(clone);
          });
          count += originals.length;
        }
      }
    },
  );
}

/* ===== custom-faq-accordion ===== */
if (!customElements.get('custom-faq-accordion')) {
  customElements.define(
    'custom-faq-accordion',
    class extends HTMLElement {
      connectedCallback() {
        this.triggers = Array.from(this.querySelectorAll('.cfaq-item__trigger'));
        this.onTriggerClick = this.onTriggerClick.bind(this);
        this.triggers.forEach((trigger) => {
          trigger.addEventListener('click', this.onTriggerClick);
        });
        if (this.triggers.length && this.dataset.keepFirstOpen === 'true') {
          this.openItem(this.triggers[0]);
        }
      }
      disconnectedCallback() {
        this.triggers.forEach((trigger) => {
          trigger.removeEventListener('click', this.onTriggerClick);
        });
      }
      onTriggerClick(event) {
        const trigger = event.currentTarget;
        const isOpen = trigger.getAttribute('aria-expanded') === 'true';
        this.triggers.forEach((item) => this.closeItem(item));
        if (!isOpen) {
          this.openItem(trigger);
        }
      }
      openItem(trigger) {
        const panel = this.querySelector(`#${trigger.getAttribute('aria-controls')}`);
        trigger.setAttribute('aria-expanded', 'true');
        if (panel) {
          panel.setAttribute('aria-hidden', 'false');
          panel.removeAttribute('inert');
        }
      }
      closeItem(trigger) {
        const panel = this.querySelector(`#${trigger.getAttribute('aria-controls')}`);
        trigger.setAttribute('aria-expanded', 'false');
        if (panel) {
          panel.setAttribute('aria-hidden', 'true');
          panel.setAttribute('inert', '');
        }
      }
    },
  );
}

/* ===== serving-calculator ===== */
if (!customElements.get('serving-calculator')) {
  customElements.define(
    'serving-calculator',
    class extends HTMLElement {
      connectedCallback() {
        this.minDogs = 1;
        this.maxDogs = 12;
        this.collapseThreshold = 6;
        this.dogsExpanded = false;
        this.unit = 'kg';
        this.dogNames = [];
        this.dogWeights = [];
        this.onRootClick = this.onRootClick.bind(this);
        this.onRootInput = this.onRootInput.bind(this);

        if (!this.loadConfig()) {
          return;
        }

        this.root = this.querySelector('[data-sc-root]');
        this.dogsContainer = this.querySelector('[data-sc-dogs]');
        this.dogTemplate = this.querySelector('[data-sc-dog-template]');
        this.dogCountEl = this.querySelector('[data-sc-dog-count]');
        this.resultsEl = this.querySelector('[data-sc-results]');
        this.summaryEl = this.querySelector('[data-sc-summary]');
        this.dogCardsEl = this.querySelector('[data-sc-dog-cards]');
        this.bannerEl = this.querySelector('[data-sc-banner]');
        this.emptyStateEl = this.querySelector('[data-sc-empty-state]');
        this.addToCartBtn = this.querySelector('[data-sc-add-to-cart]');
        this.cartStatusEl = this.querySelector('[data-sc-cart-status]');
        this.cartForm = this.querySelector('[data-sc-cart-form]');
        this.cartQuantityInput = this.querySelector('[data-sc-cart-quantity-input]');
        this.cartPropertiesEl = this.querySelector('[data-sc-cart-properties]');

        if (!this.root || !this.dogsContainer || !this.dogTemplate) {
          return;
        }

        this.root.hidden = false;
        this.dogCount = 1;
        this.lastResults = null;

        this.dogsToggleBtn = this.createToggleButton('sc__dogs-toggle', 'data-sc-dogs-toggle');
        this.dogsContainer.insertAdjacentElement('afterend', this.dogsToggleBtn);

        if (this.dogCardsEl) {
          this.cardsToggleBtn = this.createToggleButton('sc__dogs-toggle', 'data-sc-cards-toggle');
          this.dogCardsEl.insertAdjacentElement('afterend', this.cardsToggleBtn);
        }

        this.addEventListener('click', this.onRootClick);
        this.addEventListener('input', this.onRootInput);
        this.onCartFormAdded = this.onCartFormAdded.bind(this);
        this.cartForm?.addEventListener('add-to-cart', this.onCartFormAdded);

        this.renderDogInputs();
        this.recalculate();
      }

      disconnectedCallback() {
        this.removeEventListener('click', this.onRootClick);
        this.removeEventListener('input', this.onRootInput);
        this.cartForm?.removeEventListener('add-to-cart', this.onCartFormAdded);
        window.clearTimeout(this.addToCartTimeout);
      }

      formatServing(n, long) {
        const unit = (this.config && this.config.servingUnit) || 'scps';
        if (unit === 'tsp') {
          const whole = Math.floor(n + 1e-9);
          const frac = n - whole;
          const map = { '0.25': '¼', '0.5': '½', '0.75': '¾' };
          const key = Object.keys(map).find((k) => Math.abs(frac - parseFloat(k)) < 0.01);
          const txt = key ? `${whole ? whole : ''}${map[key]}` : this.formatTrimmed(n, 2);
          return `${txt} tsp`;
        }
        return `${this.formatTrimmed(n, 1)} ${long ? 'scoops' : 'scps'}`;
      }

      loadConfig() {
        const configTag = this.querySelector('[data-serving-calculator-config]');
        if (!configTag) {
          return false;
        }
        try {
          this.config = JSON.parse(configTag.textContent);
        } catch (error) {
          return false;
        }
        if (!this.config || !Array.isArray(this.config.bands) || this.config.bands.length === 0) {
          return false;
        }

        if (this.config.labels) {
          Object.keys(this.config.labels).forEach((key) => {
            this.config.labels[key] = this.decodeHtmlEntities(this.config.labels[key]);
          });
        }

        this.config.bands = this.config.bands
          .map((band) => this.parseBand(band))
          .filter(Boolean);

        if (this.config.bands.length === 0) {
          return false;
        }

        this.config.bands.sort((a, b) => a.minKg - b.minKg);
        return true;
      }

      createToggleButton(className, attr) {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = className;
        button.setAttribute(attr, '');
        button.hidden = true;
        return button;
      }

      updateToggleButton(button, totalCount) {
        if (!button) {
          return;
        }
        if (totalCount <= this.collapseThreshold) {
          button.hidden = true;
          return;
        }
        button.hidden = false;
        const hiddenCount = totalCount - this.collapseThreshold;
        button.textContent = this.dogsExpanded
          ? 'Show less'
          : `Show ${hiddenCount} more ${hiddenCount === 1 ? 'dog' : 'dogs'}`;
      }

      decodeHtmlEntities(str) {
        if (typeof str !== 'string' || str.indexOf('&') === -1) {
          return str;
        }
        const textarea = document.createElement('textarea');
        textarea.innerHTML = str;
        return textarea.value;
      }

      parseNumber(raw) {
        const value = parseFloat(raw);
        return Number.isFinite(value) ? value : 0;
      }

      parseValue(raw) {
        const number = this.parseNumber(raw);
        const unitMatch = String(raw == null ? '' : raw).match(/[a-zA-Z]+\s*$/);
        const unit = unitMatch ? unitMatch[0].trim() : 'g';
        return { number, unit };
      }

      parseBand(band) {
        const rangeMatch = String(band.weightRange || '').match(/([\d.]+)\s*-\s*([\d.]+)/);
        if (!rangeMatch) {
          return null;
        }
        return {
          label: band.weightRange,
          minKg: parseFloat(rangeMatch[1]),
          maxKg: parseFloat(rangeMatch[2]),
          scoopsPerDay: this.parseNumber(band.scoopsPerDay),
          gramsPerServing: this.parseNumber(band.gramsPerServing),
          kcalPerServing: this.parseNumber(band.kcalPerServing),
          pouchWeight: this.parseNumber(band.pouchWeight),
          constituents: [
            { key: 'crude_protein', ...this.parseValue(band.crudeProtein) },
            { key: 'crude_fat', ...this.parseValue(band.crudeFat) },
            { key: 'crude_fibre', ...this.parseValue(band.crudeFibre) },
            { key: 'crude_ash', ...this.parseValue(band.crudeAsh) },
            { key: 'moisture', ...this.parseValue(band.moisture) },
            { key: 'omega_3s', ...this.parseValue(band.omega3s) },
          ],
        };
      }

      onRootClick(event) {
        const dogIncrement = event.target.closest('[data-sc-dog-increment]');
        const dogDecrement = event.target.closest('[data-sc-dog-decrement]');
        const unitBtn = event.target.closest('[data-sc-unit]');
        const addToCartBtn = event.target.closest('[data-sc-add-to-cart]');
        const dogsToggle = event.target.closest('[data-sc-dogs-toggle]');
        const cardsToggle = event.target.closest('[data-sc-cards-toggle]');

        if (dogIncrement) {
          this.setDogCount(this.dogCount + 1);
        } else if (dogDecrement) {
          this.setDogCount(this.dogCount - 1);
        } else if (unitBtn) {
          this.setUnit(unitBtn.dataset.scUnit);
        } else if (addToCartBtn) {
          this.onAddToCart();
        } else if (dogsToggle || cardsToggle) {
          this.dogsExpanded = !this.dogsExpanded;
          this.applyDogsCollapse();
          if (this.lastResults) {
            this.applyCardsCollapse();
          }
        }
      }

      onRootInput(event) {
        if (event.target.closest('[data-sc-dog-input]')) {
          this.recalculate();
        }
      }

      setDogCount(nextCount) {
        const clamped = Math.min(this.maxDogs, Math.max(this.minDogs, nextCount));
        if (clamped === this.dogCount) {
          return;
        }
        this.dogCount = clamped;
        this.renderDogInputs();
        this.recalculate();
      }

      setUnit(nextUnit) {
        if (nextUnit === this.unit) {
          return;
        }
        const weightInputs = this.dogsContainer.querySelectorAll('[data-sc-weight-input]');
        weightInputs.forEach((input) => {
          const rawValue = parseFloat(input.value);
          if (Number.isFinite(rawValue) && rawValue > 0) {
            const converted = nextUnit === 'lb' ? rawValue * 2.20462 : rawValue / 2.20462;
            input.value = Math.round(converted * 10) / 10;
          }
        });
        this.unit = nextUnit;
        this.querySelectorAll('[data-sc-unit]').forEach((btn) => {
          const isActive = btn.dataset.scUnit === nextUnit;
          btn.classList.toggle('is-active', isActive);
          btn.setAttribute('aria-pressed', String(isActive));
        });
        this.querySelectorAll('[data-sc-weight-unit]').forEach((label) => {
          label.textContent = nextUnit;
        });
        this.recalculate();
      }

      renderDogInputs() {
        const existingCards = Array.from(this.dogsContainer.querySelectorAll('[data-sc-dog-input]'));
        const currentNames = existingCards.map((card) => card.querySelector('[data-sc-name-input]').value);
        const currentWeights = existingCards.map((card) => card.querySelector('[data-sc-weight-input]').value);

        this.dogsContainer.innerHTML = '';

        for (let index = 0; index < this.dogCount; index += 1) {
          const fragment = this.dogTemplate.content.cloneNode(true);
          const nameInput = fragment.querySelector('[data-sc-name-input]');
          const weightInput = fragment.querySelector('[data-sc-weight-input]');
          const weightUnitLabel = fragment.querySelector('[data-sc-weight-unit]');
          const nameLabel = fragment.querySelector('.sc__field-label');

          nameInput.value = currentNames[index] || '';
          nameInput.placeholder = this.dogCount > 1 ? `${this.config.labels.dogNamePlaceholder} ${index + 1}` : this.config.labels.dogNamePlaceholder;
          nameInput.setAttribute('aria-label', nameLabel ? `${nameLabel.textContent} ${index + 1}` : this.config.labels.dogNamePlaceholder);
          weightInput.value = currentWeights[index] || '';
          weightInput.setAttribute('aria-label', `${this.config.labels.weight} ${index + 1}`);
          if (weightUnitLabel) {
            weightUnitLabel.textContent = this.unit;
          }

          this.dogsContainer.appendChild(fragment);
        }

        if (this.dogCountEl) {
          this.dogCountEl.textContent = String(this.dogCount);
        }
        this.querySelectorAll('[data-sc-dog-decrement]').forEach((btn) => {
          btn.disabled = this.dogCount <= this.minDogs;
        });
        this.querySelectorAll('[data-sc-dog-increment]').forEach((btn) => {
          btn.disabled = this.dogCount >= this.maxDogs;
        });

        this.applyDogsCollapse();
      }

      applyDogsCollapse() {
        const cards = Array.from(this.dogsContainer.querySelectorAll('[data-sc-dog-input]'));
        cards.forEach((card, index) => {
          card.hidden = !this.dogsExpanded && index >= this.collapseThreshold;
        });
        this.updateToggleButton(this.dogsToggleBtn, cards.length);
      }

      applyCardsCollapse() {
        if (!this.dogCardsEl) {
          return;
        }
        const cards = Array.from(this.dogCardsEl.querySelectorAll('[data-dog-card]'));
        cards.forEach((card, index) => {
          card.hidden = !this.dogsExpanded && index >= this.collapseThreshold;
        });
        this.updateToggleButton(this.cardsToggleBtn, cards.length);
      }

      weightToKg(rawValue) {
        return this.unit === 'lb' ? rawValue / 2.20462 : rawValue;
      }

      findBand(weightKg) {
        const bands = this.config.bands;
        const firstBand = bands[0];
        const lastBand = bands[bands.length - 1];

        if (weightKg < firstBand.minKg) {
          return { status: 'under', band: null, limit: firstBand.minKg };
        }
        if (weightKg > lastBand.maxKg) {
          return { status: 'over', band: null, limit: lastBand.maxKg };
        }
        const match = bands.find((band) => weightKg >= band.minKg && weightKg <= band.maxKg);
        return match ? { status: 'ok', band: match } : { status: 'over', band: null, limit: lastBand.maxKg };
      }

      formatTrimmed(value, decimals) {
        const rounded = Number(value.toFixed(decimals));
        return String(rounded);
      }

      recalculate() {
        const dogCards = Array.from(this.dogsContainer.querySelectorAll('[data-sc-dog-input]'));
        const dogs = [];
        let hasAnyWeight = false;
        let hasError = false;

        dogCards.forEach((card, index) => {
          const nameInput = card.querySelector('[data-sc-name-input]');
          const weightInput = card.querySelector('[data-sc-weight-input]');
          const errorEl = card.querySelector('[data-sc-dog-error]');
          const rawValue = parseFloat(weightInput.value);

          errorEl.hidden = true;
          errorEl.textContent = '';

          if (!Number.isFinite(rawValue) || rawValue <= 0) {
            return;
          }

          hasAnyWeight = true;
          const weightKg = this.weightToKg(rawValue);
          const lookup = this.findBand(weightKg);

          if (lookup.status !== 'ok') {
            hasError = true;
            const limitLabel = lookup.status === 'under' ? this.config.labels.underMin : this.config.labels.overMax;
            errorEl.textContent = limitLabel.replace('__MIN__', lookup.limit).replace('__MAX__', lookup.limit);
            errorEl.hidden = false;
            return;
          }

          dogs.push({
            index,
            name: nameInput.value.trim() || `Dog ${index + 1}`,
            weightRaw: rawValue,
            weightKg,
            band: lookup.band,
          });
        });

        if (!hasAnyWeight || hasError || dogs.length !== dogCards.length) {
          this.lastResults = null;
          this.resultsEl.hidden = true;
          this.emptyStateEl.hidden = false;
          return;
        }

        this.emptyStateEl.hidden = true;
        this.resultsEl.hidden = false;
        this.lastResults = this.computeResults(dogs);
        this.renderResults(this.lastResults);
      }

      computeResults(dogs) {
        const supplyPeriodDays = this.config.supplyPeriodDays || 30;
        const pouchWeight = dogs[0].band.pouchWeight;

        const perDog = dogs.map((dog) => ({
          ...dog,
          scoopsPerDay: dog.band.scoopsPerDay,
          gramsPerDay: dog.band.gramsPerServing,
          kcalPerDay: dog.band.kcalPerServing,
          daysPouchLasts: Math.floor(dog.band.pouchWeight / dog.band.gramsPerServing),
          constituents: dog.band.constituents,
        }));

        const combinedScoopsPerDay = perDog.reduce((sum, dog) => sum + dog.scoopsPerDay, 0);
        const combinedGramsPerDay = perDog.reduce((sum, dog) => sum + dog.gramsPerDay, 0);
        const servingsPerPouch = pouchWeight / combinedGramsPerDay;
        const combinedDaysPouchLasts = servingsPerPouch;
        const weeksSupply = Math.round(combinedDaysPouchLasts / 7);
        const recommendedPouches = Math.max(1, Math.ceil((supplyPeriodDays * combinedGramsPerDay) / pouchWeight));

        return {
          perDog,
          combinedScoopsPerDay,
          servingsPerPouch,
          combinedDaysPouchLasts,
          weeksSupply,
          recommendedPouches,
          supplyPeriodDays,
        };
      }

      renderResults(results) {
        const summaryTemplate = this.querySelector('[data-sc-summary-template]');
        const cardTemplate = this.querySelector('[data-sc-dog-card-template]');

        const labels = this.config.labels;
        this.summaryEl.innerHTML = '';
        [
          { value: `${this.formatServing(results.combinedScoopsPerDay)}`, label: labels.combinedDaily },
          { value: this.formatTrimmed(results.servingsPerPouch, 1), label: labels.servingsPerPouch },
          { value: `${this.formatTrimmed(results.combinedDaysPouchLasts, 0)} days`, label: labels.pouchLasts },
          { value: `${results.weeksSupply} weeks`, label: labels.weeksSupply },
        ].forEach((stat) => {
          const fragment = summaryTemplate.content.cloneNode(true);
          fragment.querySelector('[data-value]').textContent = stat.value;
          fragment.querySelector('[data-label]').textContent = stat.label;
          this.summaryEl.appendChild(fragment);
        });

        this.dogCardsEl.innerHTML = '';
        const constituentLabels = {
          crude_protein: labels.crudeProtein,
          crude_fat: labels.crudeFat,
          crude_fibre: labels.crudeFibre,
          crude_ash: labels.crudeAsh,
          moisture: labels.moisture,
          omega_3s: labels.omega3s,
        };

        results.perDog.forEach((dog) => {
          const fragment = cardTemplate.content.cloneNode(true);
          fragment.querySelector('[data-dog-title]').textContent = `${dog.name} (${this.formatTrimmed(dog.weightRaw, 1)}${this.unit})`;
          fragment.querySelector('[data-dog-serving]').textContent = `${this.formatServing(dog.scoopsPerDay)} (${this.formatTrimmed(dog.gramsPerDay, 1)}g)`;
          fragment.querySelector('[data-dog-kcal]').textContent = `${this.formatTrimmed(dog.kcalPerDay, 1)}kcal`;
          fragment.querySelector('[data-dog-days]').textContent = `${dog.daysPouchLasts} days`;

          const constituentsBody = fragment.querySelector('[data-dog-constituents]');
          dog.constituents.filter((constituent) => constituent.number > 0).forEach((constituent) => {
            const row = document.createElement('div');
            row.className = 'sc__constituents-row';
            row.innerHTML = `<span>${constituentLabels[constituent.key]}</span><span>${this.formatTrimmed(constituent.number, 1)}${constituent.unit}</span>`;
            constituentsBody.appendChild(row);
          });

          this.dogCardsEl.appendChild(fragment);
        });

        this.applyCardsCollapse();

        const pouchLabel = (results.recommendedPouches === 1 ? labels.pouchOne : labels.pouchOther).replace('__COUNT__', results.recommendedPouches);
        const supplyLabel = labels.recommendedSupply.replace('__DAYS__', results.supplyPeriodDays);
        this.bannerEl.innerHTML = `<span class="sc__banner-value">${pouchLabel}</span><span class="sc__banner-label">${supplyLabel}</span>`;

        if (this.addToCartBtn) {
          this.addToCartBtn.disabled = false;
        }
      }

      onAddToCart() {
        if (!this.lastResults || !this.cartForm || this.addToCartBtn.disabled) {
          return;
        }

        if (this.cartQuantityInput) {
          this.cartQuantityInput.value = this.lastResults.recommendedPouches;
        }

        if (this.cartPropertiesEl) {
          this.cartPropertiesEl.innerHTML = '';
          if (this.config.storeLineItemProperties) {
            this.lastResults.perDog.forEach((dog, index) => {
              const input = document.createElement('input');
              input.type = 'hidden';
              input.name = `properties[Dog ${index + 1}]`;
              input.value = `${dog.name}, ${this.formatTrimmed(dog.weightRaw, 1)}${this.unit}, ${this.formatServing(dog.scoopsPerDay, true)}/day`;
              this.cartPropertiesEl.appendChild(input);
            });
          }
        }

        const form = this.cartForm.querySelector('form');
        if (!form || typeof form.requestSubmit !== 'function') {
          console.warn('[serving-calculator] add-to-cart form not found — check that snippets/serving-calculator.liquid rendered a <form> inside [data-sc-cart-form].');
          if (this.cartStatusEl) {
            this.cartStatusEl.hidden = false;
            this.cartStatusEl.textContent = this.config.labels.addToCartError;
          }
          return;
        }

        this.addToCartBtn.disabled = true;
        if (this.cartStatusEl) {
          this.cartStatusEl.hidden = false;
          this.cartStatusEl.textContent = this.config.labels.adding;
        }

        window.clearTimeout(this.addToCartTimeout);
        this.addToCartTimeout = window.setTimeout(() => {
          if (this.addToCartBtn.disabled) {
            console.warn('[serving-calculator] add-to-cart timed out waiting for the "add-to-cart" event — check the network tab / console for a cart_add_url error.');
            this.addToCartBtn.disabled = false;
            if (this.cartStatusEl) {
              this.cartStatusEl.textContent = this.config.labels.addToCartError;
            }
          }
        }, 8000);

        form.requestSubmit();
      }

      onCartFormAdded() {
        window.clearTimeout(this.addToCartTimeout);
        this.addToCartBtn.disabled = false;
        if (this.cartStatusEl) {
          this.cartStatusEl.textContent = this.config.labels.added || '';
        }
        const drawer = document.getElementById('site-cart-sidebar');
        if (drawer && typeof drawer.show === 'function') {
          drawer.show();
        } else if (typeof window.refreshCart === 'function') {
          window.refreshCart(true);
        }
      }
    },
  );
}

// Ingredients flip grid (sections/ingredients-grid.liquid)
(function () {
  function initIngredientFlip() {
    document.querySelectorAll('.ing__card:not([data-flip-init])').forEach(function (card) {
      card.setAttribute('data-flip-init', '');
      card.addEventListener('click', function () {
        card.classList.toggle('is-flipped');
      });
    });
  }
  document.addEventListener('DOMContentLoaded', initIngredientFlip);
  document.addEventListener('shopify:section:load', initIngredientFlip);
})();

/* ===== nutrition-calculator ===== */
if (!customElements.get('nutrition-calculator')) {
  const NC_MAX_PET_PRODUCTS = 3;
  customElements.define(
    'nutrition-calculator',
    class extends HTMLElement {
      connectedCallback() {
        if (!this.loadConfig()) {
          return;
        }

        this.root = this.querySelector('[data-nc-root]');
        this.petsContainer = this.querySelector('[data-nc-pets]');
        this.petTemplate = this.querySelector('[data-nc-pet-template]');
        this.productTileTemplate = this.querySelector('[data-nc-product-tile-template]');
        this.constituentRowTemplate = this.querySelector('[data-nc-constituent-row-template]');
        this.modal = this.querySelector('[data-nc-modal]');
        this.modalGrid = this.querySelector('[data-nc-modal-grid]');
        this.modalItemTemplate = this.querySelector('[data-nc-modal-item-template]');

        if (!this.root || !this.petsContainer || !this.petTemplate) {
          return;
        }

        this.root.hidden = false;
        this.activePetEl = null;
        this.lastOpenerBtn = null;

        this.onClick = this.onClick.bind(this);
        this.onInput = this.onInput.bind(this);
        this.onKeydown = this.onKeydown.bind(this);
        this.addEventListener('click', this.onClick);
        this.addEventListener('input', this.onInput);
        this.addEventListener('keydown', this.onKeydown);

        this.addPet();
      }

      disconnectedCallback() {
        this.removeEventListener('click', this.onClick);
        this.removeEventListener('input', this.onInput);
        this.removeEventListener('keydown', this.onKeydown);
      }

      loadConfig() {
        const configTag = this.querySelector('[data-nc-config]');
        if (!configTag) {
          return false;
        }
        try {
          this.config = JSON.parse(configTag.textContent);
        } catch (error) {
          return false;
        }
        if (!this.config || !Array.isArray(this.config.products)) {
          return false;
        }

        if (this.config.labels) {
          Object.keys(this.config.labels).forEach((key) => {
            this.config.labels[key] = this.decodeHtmlEntities(this.config.labels[key]);
          });
        }

        this.config.products = this.config.products
          .map((product) => ({
            ...product,
            bands: (product.bands || []).map((band) => this.parseBand(band)).filter(Boolean),
          }))
          .filter((product) => product.bands.length > 0);

        return true;
      }

      decodeHtmlEntities(str) {
        if (typeof str !== 'string' || str.indexOf('&') === -1) {
          return str;
        }
        const textarea = document.createElement('textarea');
        textarea.innerHTML = str;
        return textarea.value;
      }

      parseNumber(raw) {
        const value = parseFloat(raw);
        return Number.isFinite(value) ? value : 0;
      }

      parseValue(raw) {
        const number = this.parseNumber(raw);
        const unitMatch = String(raw == null ? '' : raw).match(/[a-zA-Z%]+\s*$/);
        const unit = unitMatch ? unitMatch[0].trim() : 'g';
        return { number, unit };
      }

      parseBand(band) {
        const rangeMatch = String(band.weightRange || '').match(/([\d.]+)\s*-\s*([\d.]+)/);
        if (!rangeMatch) {
          return null;
        }
        const parsed = {
          minKg: parseFloat(rangeMatch[1]),
          maxKg: parseFloat(rangeMatch[2]),
          kcalPerServing: this.parseNumber(band.kcalPerServing),
          constituents: {
            crude_protein: this.parseValue(band.crudeProtein),
            crude_fat: this.parseValue(band.crudeFat),
            crude_fibre: this.parseValue(band.crudeFibre),
            crude_ash: this.parseValue(band.crudeAsh),
            iodine: this.parseValue(band.iodine),
            moisture: this.parseValue(band.moisture),
            omega_3s: this.parseValue(band.omega3s),
          },
        };

        const recommendedFields = [
          band.recommendedCrudeProtein,
          band.recommendedCrudeFat,
          band.recommendedCrudeFibre,
          band.recommendedCrudeAsh,
          band.recommendedIodine,
          band.recommendedMoisture,
          band.recommendedOmega3s,
        ];
        if (recommendedFields.some((value) => value != null && String(value).trim() !== '')) {
          parsed.recommended = {
            crude_protein: this.parseValue(band.recommendedCrudeProtein),
            crude_fat: this.parseValue(band.recommendedCrudeFat),
            crude_fibre: this.parseValue(band.recommendedCrudeFibre),
            crude_ash: this.parseValue(band.recommendedCrudeAsh),
            iodine: this.parseValue(band.recommendedIodine),
            moisture: this.parseValue(band.recommendedMoisture),
            omega_3s: this.parseValue(band.recommendedOmega3s),
          };
        }

        return parsed;
      }

      findBand(bands, weightKg) {
        return bands.find((band) => weightKg >= band.minKg && weightKg <= band.maxKg) || null;
      }

      formatTrimmed(value, decimals) {
        const rounded = Number(value.toFixed(decimals));
        return String(rounded);
      }

      onClick(event) {
        const addPetBtn = event.target.closest('[data-nc-add-pet]');
        const openPickerBtn = event.target.closest('[data-nc-open-picker]');
        const removePetBtn = event.target.closest('[data-nc-remove-pet]');
        const removeProductBtn = event.target.closest('[data-nc-remove-product]');
        const modalClose = event.target.closest('[data-nc-modal-close]');
        const modalItem = event.target.closest('[data-nc-modal-item]');

        if (addPetBtn) {
          this.addPet();
        } else if (openPickerBtn) {
          this.openPicker(event.target.closest('[data-nc-pet]'), openPickerBtn);
        } else if (removePetBtn) {
          this.removePet(event.target.closest('[data-nc-pet]'));
        } else if (removeProductBtn) {
          const tile = event.target.closest('[data-nc-product-tile]');
          const petEl = event.target.closest('[data-nc-pet]');
          this.toggleProduct(petEl, tile.dataset.productId);
        } else if (modalClose) {
          this.closeModal();
        } else if (modalItem) {
          this.toggleProduct(this.activePetEl, modalItem.dataset.productId);
          this.renderModalGrid();
        }
      }

      onInput(event) {
        if (event.target.closest('[data-nc-pet]')) {
          this.recalculatePet(event.target.closest('[data-nc-pet]'));
        }
      }

      onKeydown(event) {
        if (!this.modal || this.modal.hidden) {
          return;
        }
        if (event.key === 'Escape') {
          this.closeModal();
          return;
        }
        if (event.key === 'Tab') {
          const focusable = Array.from(
            this.modal.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'),
          ).filter((el) => !el.hidden);
          if (focusable.length === 0) {
            return;
          }
          const first = focusable[0];
          const last = focusable[focusable.length - 1];
          if (event.shiftKey && document.activeElement === first) {
            event.preventDefault();
            last.focus();
          } else if (!event.shiftKey && document.activeElement === last) {
            event.preventDefault();
            first.focus();
          }
        }
      }

      addPet() {
        const fragment = this.petTemplate.content.cloneNode(true);
        const petEl = fragment.querySelector('[data-nc-pet]');
        petEl.selectedProductIds = [];

        petEl.querySelector('[data-nc-name-label]').textContent = this.config.labels.dogNameShort;
        petEl.querySelector('[data-nc-name-label-optional]').textContent = this.config.labels.optionalSuffix;
        petEl.querySelector('[data-nc-pet-name]').placeholder = this.config.labels.dogNamePlaceholder;
        petEl.querySelector('[data-nc-weight-label]').textContent = this.config.labels.weight;
        petEl.querySelector('[data-nc-products-heading]').textContent = this.config.labels.pickerHeading;
        petEl.querySelector('[data-nc-open-picker]').setAttribute('aria-label', this.config.labels.addProduct);
        petEl.querySelector('[data-nc-results-heading]').textContent = this.config.labels.resultsHeading;
        petEl.querySelector('[data-nc-kcal-label]').textContent = this.config.labels.caloriesPerDay;
        petEl.querySelector('[data-nc-constituents-heading]').textContent = this.config.labels.analyticalConstituents;
        petEl.querySelector('[data-nc-th-constituents]').textContent = this.config.labels.constituents;
        petEl.querySelector('[data-nc-th-recommended]').textContent = this.config.labels.recommendedDaily;
        petEl.querySelector('[data-nc-th-serving]').textContent = this.config.labels.dailyServing;
        petEl.querySelector('[data-nc-remove-pet]').textContent = this.config.labels.removePet;

        this.petsContainer.appendChild(fragment);
        this.updateRemovePetVisibility();
      }

      removePet(petEl) {
        if (!petEl || this.petsContainer.children.length <= 1) {
          return;
        }
        petEl.remove();
        this.updateRemovePetVisibility();
      }

      updateRemovePetVisibility() {
        const pets = Array.from(this.petsContainer.children);
        pets.forEach((petEl) => {
          const btn = petEl.querySelector('[data-nc-remove-pet]');
          if (btn) {
            btn.hidden = pets.length <= 1;
          }
        });
      }

      renderProductTiles(petEl) {
        const list = petEl.querySelector('[data-nc-products-list]');
        const openPickerBtn = petEl.querySelector('[data-nc-open-picker]');
        if (openPickerBtn) {
          openPickerBtn.hidden = petEl.selectedProductIds.length >= NC_MAX_PET_PRODUCTS;
        }
        list.innerHTML = '';
        petEl.selectedProductIds.forEach((productId) => {
          const product = this.config.products.find((p) => String(p.id) === String(productId));
          if (!product) {
            return;
          }
          const fragment = this.productTileTemplate.content.cloneNode(true);
          const tile = fragment.querySelector('[data-nc-product-tile]');
          tile.dataset.productId = productId;
          const img = fragment.querySelector('[data-nc-product-image]');
          img.src = product.image || '';
          img.alt = product.title;
          fragment.querySelector('[data-nc-product-title]').textContent = product.title;
          fragment.querySelector('[data-nc-remove-product]').setAttribute('aria-label', `${this.config.labels.removeProduct}: ${product.title}`);
          list.appendChild(fragment);
        });
      }

      toggleProduct(petEl, productId) {
        if (!petEl) {
          return;
        }
        const index = petEl.selectedProductIds.findIndex((id) => String(id) === String(productId));
        if (index === -1) {
          if (petEl.selectedProductIds.length >= NC_MAX_PET_PRODUCTS) {
            return;
          }
          petEl.selectedProductIds.push(productId);
        } else {
          petEl.selectedProductIds.splice(index, 1);
        }
        this.renderProductTiles(petEl);
        this.recalculatePet(petEl);
      }

      openPicker(petEl, openerBtn) {
        if (!petEl || !this.modal) {
          return;
        }
        this.activePetEl = petEl;
        this.lastOpenerBtn = openerBtn;
        this.renderModalGrid();
        this.modal.hidden = false;
        this.modal.removeAttribute('inert');
        const firstItem = this.modalGrid.querySelector('[data-nc-modal-item]');
        (firstItem || this.modal.querySelector('[data-nc-modal-close]'))?.focus();
      }

      closeModal() {
        if (!this.modal) {
          return;
        }
        this.modal.hidden = true;
        this.modal.setAttribute('inert', '');
        this.lastOpenerBtn?.focus();
        this.activePetEl = null;
      }

      renderModalGrid() {
        if (!this.modalGrid) {
          return;
        }
        this.modalGrid.innerHTML = '';

        if (this.config.products.length === 0) {
          const empty = document.createElement('p');
          empty.className = 'nc-empty-state';
          empty.textContent = this.config.labels.pickerEmpty;
          this.modalGrid.appendChild(empty);
          return;
        }

        const selected = this.activePetEl ? this.activePetEl.selectedProductIds : [];
        const atMax = selected.length >= NC_MAX_PET_PRODUCTS;
        this.config.products.forEach((product) => {
          const fragment = this.modalItemTemplate.content.cloneNode(true);
          const item = fragment.querySelector('[data-nc-modal-item]');
          const isAdded = selected.some((id) => String(id) === String(product.id));
          item.dataset.productId = product.id;
          item.classList.toggle('is-added', isAdded);
          item.classList.toggle('is-disabled', !isAdded && atMax);
          item.disabled = !isAdded && atMax;
          const overlay = fragment.querySelector('[data-nc-modal-item-overlay]');
          if (overlay) {
            overlay.hidden = !isAdded;
          }
          const img = fragment.querySelector('[data-nc-modal-item-image]');
          img.src = product.image || '';
          img.alt = product.title;
          fragment.querySelector('[data-nc-modal-item-title]').textContent = product.title;
          this.modalGrid.appendChild(fragment);
        });
      }

      recalculatePet(petEl) {
        const nameInput = petEl.querySelector('[data-nc-pet-name]');
        const weightInput = petEl.querySelector('[data-nc-pet-weight]');
        const errorEl = petEl.querySelector('[data-nc-pet-error]');
        const resultsEl = petEl.querySelector('[data-nc-results]');
        const rawWeight = parseFloat(weightInput.value);

        errorEl.hidden = true;
        errorEl.textContent = '';
        resultsEl.hidden = true;

        if (!Number.isFinite(rawWeight) || rawWeight <= 0) {
          errorEl.textContent = this.config.labels.enterWeight;
          errorEl.hidden = false;
          return;
        }

        if (petEl.selectedProductIds.length === 0) {
          errorEl.textContent = this.config.labels.noProducts;
          errorEl.hidden = false;
          return;
        }

        const matchedBands = petEl.selectedProductIds
          .map((productId) => this.config.products.find((p) => String(p.id) === String(productId)))
          .filter(Boolean)
          .map((product) => this.findBand(product.bands, rawWeight))
          .filter(Boolean);

        if (matchedBands.length === 0) {
          errorEl.textContent = this.config.labels.noMatch;
          errorEl.hidden = false;
          return;
        }

        const nutrientKeys = ['crude_protein', 'crude_fat', 'crude_fibre', 'crude_ash', 'iodine', 'moisture', 'omega_3s'];
        const totals = {};
        nutrientKeys.forEach((key) => {
          const withValue = matchedBands.find((band) => band.constituents[key].number > 0) || matchedBands[0];
          totals[key] = {
            number: matchedBands.reduce((sum, band) => sum + band.constituents[key].number, 0),
            unit: withValue.constituents[key].unit,
          };
        });
        const kcalPerDay = matchedBands.reduce((sum, band) => sum + band.kcalPerServing, 0);

        const rdiBand = matchedBands.find((band) => band.recommended) || null;
        const showRecommended = Boolean(rdiBand);

        this.renderPetResults(petEl, {
          name: nameInput.value.trim(),
          weightRaw: rawWeight,
          kcalPerDay,
          totals,
          rdiBand,
          showRecommended,
          nutrientKeys,
        });

        resultsEl.hidden = false;
      }

      renderPetResults(petEl, data) {
        const nutrientLabels = {
          crude_protein: this.config.labels.crudeProtein,
          crude_fat: this.config.labels.crudeFat,
          crude_fibre: this.config.labels.crudeFibre,
          crude_ash: this.config.labels.crudeAsh,
          iodine: this.config.labels.iodine,
          moisture: this.config.labels.moisture,
          omega_3s: this.config.labels.omega3s,
        };

        const nameTitleEl = petEl.querySelector('[data-nc-pet-name-title]');
        const optionalTitleEl = petEl.querySelector('[data-nc-pet-optional-title]');
        if (data.name) {
          nameTitleEl.textContent = data.name;
          optionalTitleEl.textContent = '';
          optionalTitleEl.hidden = true;
        } else {
          nameTitleEl.textContent = this.config.labels.dogNameShort;
          optionalTitleEl.textContent = this.config.labels.optionalSuffix;
          optionalTitleEl.hidden = false;
        }
        petEl.querySelector('[data-nc-pet-weight-title]').textContent = `(${this.formatTrimmed(data.weightRaw, 1)}KG)`;
        petEl.querySelector('[data-nc-kcal]').textContent = `${this.formatTrimmed(data.kcalPerDay, 1)}kcal`;

        const thRecommended = petEl.querySelector('[data-nc-th-recommended]');
        thRecommended.hidden = !data.showRecommended;
        petEl.querySelector('[data-nc-table-head]').classList.toggle('nc-table-head--two-col', !data.showRecommended);

        const body = petEl.querySelector('[data-nc-constituents]');
        body.innerHTML = '';
        data.nutrientKeys.forEach((key) => {
          const fragment = this.constituentRowTemplate.content.cloneNode(true);
          const row = fragment.querySelector('[data-nc-constituent-row]');
          row.classList.toggle('nc-table-row--two-col', !data.showRecommended);
          fragment.querySelector('[data-nc-row-label]').textContent = nutrientLabels[key];
          const recommendedEl = fragment.querySelector('[data-nc-row-recommended]');
          if (data.showRecommended) {
            const rdiValue = data.rdiBand.recommended[key];
            recommendedEl.textContent = `${this.formatTrimmed(rdiValue.number, 1)}${rdiValue.unit}`;
            recommendedEl.hidden = false;
          } else {
            recommendedEl.hidden = true;
          }
          const totalValue = data.totals[key];
          fragment.querySelector('[data-nc-row-serving]').textContent = `${this.formatTrimmed(totalValue.number, 1)}${totalValue.unit}`;
          body.appendChild(fragment);
        });
      }
    },
  );
}

/* ===== faq-page ===== */
if (!customElements.get('faq-page')) {
  customElements.define(
    'faq-page',
    class extends HTMLElement {
      connectedCallback() {
        this.tabBtns  = Array.from(this.querySelectorAll('.fpg-tab-btn'));
        this.panels   = Array.from(this.querySelectorAll('[data-panel]'));
        this.triggers = Array.from(this.querySelectorAll('.fpg-accordion-trigger'));
        const defaultTab = this.dataset.defaultTab
          || (this.tabBtns[0] && this.tabBtns[0].dataset.tab)
          || '';
        this._bindEvents();
        if (defaultTab) this._setTab(defaultTab);
      }

      _bindEvents() {
        this.tabBtns.forEach((btn) => {
          btn.addEventListener('click', () => this._setTab(btn.dataset.tab));
        });
        this.triggers.forEach((trigger) => {
          trigger.addEventListener('click', () => this._toggleAccordion(trigger));
        });
      }

      _setTab(tab) {
        this.tabBtns.forEach((btn) => btn.classList.toggle('is-active', btn.dataset.tab === tab));
        this.panels.forEach((panel) => {
          if (panel.dataset.panel === tab) {
            panel.removeAttribute('hidden');
            if (panel.dataset.isProduct === 'true') this._initProductPanel(panel);
          } else {
            panel.setAttribute('hidden', '');
          }
        });
        this._closeAll();
      }

      _initProductPanel(panel) {
        if (panel._productInited) {
          const productItems = Array.from(panel.querySelectorAll('.fpg-product-item'));
          if (productItems.length > 0) this._setProduct(panel, productItems[0].dataset.product, productItems);
          return;
        }
        panel._productInited = true;
        const productItems = Array.from(panel.querySelectorAll('.fpg-product-item'));
        productItems.forEach((item) => {
          item.addEventListener('click', (e) => {
            if (e.target.closest('a')) return;
            this._setProduct(panel, item.dataset.product, productItems);
          });
        });
        if (productItems.length > 0) this._setProduct(panel, productItems[0].dataset.product, productItems);
      }

      _setProduct(panel, product, productItems) {
        productItems.forEach((item) => item.classList.toggle('is-active', item.dataset.product === product));
        const faqItems = Array.from(panel.querySelectorAll('.fpg-faq-item'));
        faqItems.forEach((item) => { item.hidden = item.dataset.product !== product; });
        this._closeAll();
        if (window.innerWidth < 990) {
          const accordions = panel.querySelector('.fpg-accordions');
          if (accordions) setTimeout(() => accordions.scrollIntoView({ behavior: 'smooth', block: 'start' }), 80);
        }
      }

      _toggleAccordion(trigger) {
        const expanded = trigger.getAttribute('aria-expanded') === 'true';
        const panel    = document.getElementById(trigger.getAttribute('aria-controls'));
        trigger.setAttribute('aria-expanded', expanded ? 'false' : 'true');
        if (panel) {
          panel.setAttribute('aria-hidden', expanded ? 'true' : 'false');
          expanded ? panel.setAttribute('inert', '') : panel.removeAttribute('inert');
        }
      }

      _closeAll() {
        this.triggers.forEach((trigger) => {
          trigger.setAttribute('aria-expanded', 'false');
          const panel = document.getElementById(trigger.getAttribute('aria-controls'));
          if (panel) { panel.setAttribute('aria-hidden', 'true'); panel.setAttribute('inert', ''); }
        });
      }
    },
  );
}

/* ===== faq-page-v2 ===== */
if (!customElements.get('faq-page-v2')) {
  customElements.define(
    'faq-page-v2',
    class extends HTMLElement {
      connectedCallback() {
        this.tabBtns  = Array.from(this.querySelectorAll('.fpg-tab-btn'));
        this.panels   = Array.from(this.querySelectorAll('[data-panel]'));
        this.triggers = Array.from(this.querySelectorAll('.fpg2-trigger'));
        const defaultTab = this.dataset.defaultTab
          || (this.tabBtns[0] && this.tabBtns[0].dataset.tab)
          || '';
        this._bindEvents();
        if (defaultTab) this._setTab(defaultTab);
      }

      _bindEvents() {
        this.tabBtns.forEach((btn) => btn.addEventListener('click', () => this._setTab(btn.dataset.tab)));
        this.triggers.forEach((trigger) => trigger.addEventListener('click', () => this._toggleAccordion(trigger)));
      }

      _setTab(tab) {
        this.tabBtns.forEach((btn) => btn.classList.toggle('is-active', btn.dataset.tab === tab));
        this.panels.forEach((panel) => {
          panel.dataset.panel === tab ? panel.removeAttribute('hidden') : panel.setAttribute('hidden', '');
        });
        this._closeAll();
      }

      _toggleAccordion(trigger) {
        const expanded = trigger.getAttribute('aria-expanded') === 'true';
        this._closeAll();
        if (expanded) return;
        const panel = document.getElementById(trigger.getAttribute('aria-controls'));
        trigger.setAttribute('aria-expanded', 'true');
        if (panel) {
          panel.setAttribute('aria-hidden', 'false');
          panel.removeAttribute('inert');
        }
      }

      _closeAll() {
        this.triggers.forEach((trigger) => {
          trigger.setAttribute('aria-expanded', 'false');
          const panel = document.getElementById(trigger.getAttribute('aria-controls'));
          if (panel) { panel.setAttribute('aria-hidden', 'true'); panel.setAttribute('inert', ''); }
        });
      }
    },
  );
}
/* ===== sticky header height =====
   Exposes the sticky header's height as --ctm-header-height so other sticky elements and
   anchor jumps sit below it. 0 when the header isn't sticky (setting off / absolute header). */
(function () {
  const header = document.querySelector('body > header');
  if (!header) return;

  const update = () => {
    const isSticky = getComputedStyle(header).position === 'sticky';
    document.documentElement.style.setProperty('--ctm-header-height', `${isSticky ? header.offsetHeight : 0}px`);
  };

  // Bottom shadow while stuck (CSS: body > header.is-stuck)
  const updateStuck = () => {
    header.classList.toggle('is-stuck', window.scrollY > 0 && getComputedStyle(header).position === 'sticky');
  };
  window.addEventListener('scroll', updateStuck, { passive: true });
  document.addEventListener('shopify:section:load', updateStuck);
  updateStuck();

  update();
  if ('ResizeObserver' in window) {
    new ResizeObserver(update).observe(header);
  } else {
    window.addEventListener('resize', update, { passive: true });
  }
  // Theme editor re-renders the header section in place (sticky setting toggled).
  document.addEventListener('shopify:section:load', update);
})();


// Mailto
function handleLink(url) {
  if (!url) return;
  const isEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(url);
  if (url.startsWith('mailto:') || isEmail) {
    window.location.href = url.startsWith('mailto:') ? url : `mailto:${url}`;
  } else {
    window.open(url, '_blank');
  }
}
/* iOS Safari repaint fix for accordion panels (e.g. serving calculator) */
(function(){
  function nudge(item){
    [60,250,450,800].forEach(function(ms){
      setTimeout(function(){
        item.style.webkitTransform='translateZ(0.01px)';
        void item.offsetHeight;
        item.style.webkitTransform='';
        window.dispatchEvent(new Event('scroll'));
      },ms);
    });
  }
  document.addEventListener('click',function(e){
    var t=e.target.closest && e.target.closest('button, summary, [role="button"], [aria-controls]');
    if(!t) return;
    var item=t.closest('.paf-item, .cfaq-item, .product-faq__item');
    if(item) nudge(item);
  });
})();
